import * as reactHookForm from 'react-hook-form';
import type { Control, FieldError, UseFormClearErrors } from 'react-hook-form';

import type { TextFieldProps } from '@material-ui/core/TextField';

import { EditableFieldTypes } from '@cloud-wave/neon-common-lib/app';

import { Input } from 'lib/common/components/Input';
import PhoneInput from 'lib/common/components/PhoneInput';
import { Option, SingleValue } from 'lib/common/components/ReactSelect/components';
import Text from 'lib/common/components/Text';

import PasswordInput from '../../components/atoms/PasswordInput';
import ReactSelectInput, { TSelectOption } from './components/ReactSelectInput';

const { Controller } = reactHookForm;

export type HookFormInputProps = TextFieldProps & {
  name: string;
  control: Control<any>;
  validationError?: FieldError;
  clearErrors?: UseFormClearErrors<any>;
  busy?: boolean;
  ignoreRows?: boolean;
  type?: keyof typeof EditableFieldTypes;
  ariaRequired?: boolean;

  options?: TSelectOption[];
  // Can be either TSelectOption[] or TSelectOption
  onSelectChange?: (value: any) => void;
  multiSelect?: boolean;
  isSearchable?: boolean;
  isLoading?: boolean;
};

const ErrorMessage = ({ message }: { message: string }) => (
  <Text type="small" color="danger">
    {message}
  </Text>
);

const HookFormInput = ({
  name,
  control,
  helperText,
  validationError,
  clearErrors,
  type,
  ignoreRows,
  options,
  onSelectChange,
  multiSelect,
  isSearchable,
  isLoading,
  ...otherProps
}: HookFormInputProps) => {
  if (type === EditableFieldTypes.PHONE) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <PhoneInput
            {...field}
            {...otherProps}
            id={otherProps.id || name}
            disabled={otherProps.disabled}
            initialValue={field.value}
            error={validationError && field.value}
            helperText={field.value && validationError?.message}
            onFocus={!otherProps.onFocus && clearErrors ? () => clearErrors(field.name) : otherProps.onFocus}
          />
        )}
      />
    );
  }

  if (type === EditableFieldTypes.PASSWORD) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <PasswordInput
            {...field}
            {...otherProps}
            autoComplete="new-password"
            error={validationError && field.value}
            helperText={
              validationError?.message && field.value ? <ErrorMessage message={validationError.message} /> : helperText
            }
            onFocus={!otherProps.onFocus && clearErrors ? () => clearErrors(field.name) : otherProps.onFocus}
          />
        )}
      />
    );
  }

  if (type === EditableFieldTypes.SELECT) {
    return (
      <ReactSelectInput
        name={name}
        label={otherProps.label}
        control={control}
        options={options || []}
        onChange={onSelectChange}
        error={validationError as FieldError}
        multiSelect={multiSelect}
        helperText={helperText}
        placeholder={otherProps.placeholder}
        isSearchable={isSearchable}
        disabled={otherProps.disabled}
        components={{
          Option: Option,
          SingleValue: SingleValue,
        }}
      />
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Input
          {...field}
          {...otherProps}
          id={otherProps.id || name}
          onBlur={(e) => {
            otherProps?.onBlur?.(e);

            field.onBlur();
          }}
          error={validationError && field.value}
          helperText={
            validationError?.message && field.value ? <ErrorMessage message={validationError.message} /> : helperText
          }
          onFocus={!otherProps.onFocus && clearErrors ? () => clearErrors(field.name) : otherProps.onFocus}
          ignoreRows={ignoreRows}
        />
      )}
    />
  );
};

const useHookForm = () => {
  return {
    ...reactHookForm,
    HookFormInput,
  };
};

export default useHookForm;
